import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Upload, Download, ChevronLeft, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TARGETS = [
  { key: 'properties', label: 'Properties' },
  { key: 'units',      label: 'Units' },
  { key: 'contacts',   label: 'Contacts' },
];

const BulkImport = () => {
  const nav = useNavigate();
  const [target, setTarget] = useState('properties');
  const [mode, setMode]     = useState('update_or_create');
  const [file, setFile]     = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult]   = useState(null);
  const [busy, setBusy]       = useState(false);

  const downloadTemplate = async (format) => {
    try {
      const res = await axios.get(`${API}/pm/import/template?type=${target}&format=${format}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `nexflow_${target}_template.${format}`;
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download template');
    }
  };

  const runPreview = async () => {
    if (!file) { toast.error('Pick a file first'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('target_type', target);
      fd.append('mode', mode);
      const { data } = await axios.post(`${API}/pm/import/preview`, fd, { withCredentials: true });
      setPreview(data);
      setResult(null);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || d?.code || 'Preview failed');
    }
    setBusy(false);
  };

  const apply = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('target_type', target);
      fd.append('mode', mode);
      const { data } = await axios.post(`${API}/pm/import/apply`, fd, { withCredentials: true });
      setResult(data);
      toast.success(`Imported: ${data.created_count} created, ${data.updated_count} updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail?.code || 'Import failed');
    }
    setBusy(false);
  };

  const downloadErrors = () => {
    if (!result?.errors?.length) return;
    const headers = Object.keys(result.errors[0].data || {});
    const lines = [['row', 'error', ...headers].join(',')];
    for (const e of result.errors) {
      const r = e.data || {};
      lines.push([e.row, `"${(e.error || '').replace(/"/g, '""')}"`, ...headers.map((h) => `"${(r[h] || '').toString().replace(/"/g, '""')}"`)].join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `nexflow_${target}_errors.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="bulk-import-page" className="p-6 space-y-6 max-w-5xl">
      <button onClick={() => nav('/pm/properties')} className="text-[#A3A3A3] text-sm hover:text-white inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Back to properties</button>
      <div>
        <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">BULK IMPORT</p>
        <h1 className="font-['Outfit'] text-3xl font-bold text-white">Import from spreadsheet</h1>
        <p className="text-[#A3A3A3] text-sm mt-1">Onboard your entire portfolio in minutes. CSV or Excel — duplicates auto-detected.</p>
      </div>

      {/* Step 1: choose target + template */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-4">
        <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em]">1. WHAT ARE YOU IMPORTING?</p>
        <div className="flex flex-wrap gap-2">
          {TARGETS.map((t) => (
            <button key={t.key} data-testid={`target-${t.key}`} onClick={() => { setTarget(t.key); setPreview(null); setResult(null); }} className={`px-4 py-2 rounded border ${target === t.key ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]' : 'border-[#404040] text-white hover:border-[#D4AF37]'}`}>{t.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-[#A3A3A3]">Need a template?</span>
          <Button data-testid="download-csv" size="sm" variant="outline" className="border-[#404040] text-white" onClick={() => downloadTemplate('csv')}><Download className="w-3 h-3 mr-1" /> CSV</Button>
          <Button data-testid="download-xlsx" size="sm" variant="outline" className="border-[#404040] text-white" onClick={() => downloadTemplate('xlsx')}><Download className="w-3 h-3 mr-1" /> Excel</Button>
        </div>
      </div>

      {/* Step 2: upload + mode */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-4">
        <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em]">2. UPLOAD YOUR FILE</p>
        <div>
          <Label>CSV or Excel file</Label>
          <input data-testid="import-file" type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }} className="block mt-1 text-sm text-[#A3A3A3] file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#D4AF37] file:text-[#0A0A0A] hover:file:bg-[#B8941F]" />
        </div>
        <div>
          <Label>How to handle duplicates</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger data-testid="import-mode" className="bg-[#0A0A0A] border-[#262626] text-white max-w-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-[#262626] text-white">
              <SelectItem value="update_or_create">Update existing + create new</SelectItem>
              <SelectItem value="create_only">Create new only (skip duplicates)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="preview-btn" disabled={!file || busy} onClick={runPreview} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]"><ArrowRight className="w-4 h-4 mr-1" /> Preview</Button>
      </div>

      {/* Step 3: preview results */}
      {preview && (
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-4" data-testid="preview-results">
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.2em]">3. REVIEW</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-3 bg-[#0A0A0A] border border-[#262626] rounded text-center">
              <p className="text-2xl font-bold text-white">{preview.rows_total}</p>
              <p className="text-[#A3A3A3] text-xs">Total rows</p>
            </div>
            <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 rounded text-center" data-testid="will-create">
              <p className="text-2xl font-bold text-emerald-300">{preview.rows_to_create}</p>
              <p className="text-emerald-300/80 text-xs">Will create</p>
            </div>
            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded text-center" data-testid="will-update">
              <p className="text-2xl font-bold text-yellow-300">{preview.rows_to_update}</p>
              <p className="text-yellow-300/80 text-xs">Will update</p>
            </div>
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded text-center" data-testid="will-error">
              <p className="text-2xl font-bold text-red-300">{preview.rows_with_errors}</p>
              <p className="text-red-300/80 text-xs">Errors</p>
            </div>
          </div>
          {preview.sample_errors?.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/20 rounded p-3">
              <p className="text-red-300 text-sm font-medium flex items-center gap-1 mb-1"><AlertTriangle className="w-4 h-4" /> First {preview.sample_errors.length} errors</p>
              <ul className="text-xs text-[#A3A3A3] space-y-1">
                {preview.sample_errors.map((e, i) => <li key={i}>Row {e.row}: <span className="text-red-300">{e.error}</span></li>)}
              </ul>
            </div>
          )}
          <Button data-testid="apply-btn" disabled={busy || preview.rows_to_create + preview.rows_to_update === 0} onClick={apply} className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]"><Upload className="w-4 h-4 mr-1" /> Apply import</Button>
        </div>
      )}

      {/* Step 4: result */}
      {result && (
        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-md p-5 space-y-3" data-testid="apply-result">
          <p className="text-emerald-300 font-bold flex items-center gap-2"><CheckCircle2 className="w-5 h-5" /> Import complete</p>
          <div className="flex items-center gap-3 flex-wrap">
            <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">{result.created_count} created</Badge>
            <Badge className="bg-yellow-500/15 text-yellow-300 border border-yellow-500/30">{result.updated_count} updated</Badge>
            <Badge className="bg-[#262626] text-[#A3A3A3] border-[#404040]">{result.skipped_count} skipped</Badge>
            {result.error_count > 0 && <Badge className="bg-red-500/15 text-red-300 border border-red-500/30">{result.error_count} errors</Badge>}
          </div>
          {result.error_count > 0 && (
            <Button data-testid="download-errors" size="sm" variant="outline" className="border-[#404040] text-white" onClick={downloadErrors}><Download className="w-3 h-3 mr-1" /> Download error report</Button>
          )}
          <Button data-testid="go-properties" size="sm" className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#B8941F]" onClick={() => nav('/pm/properties')}>Go to Properties</Button>
        </div>
      )}
    </div>
  );
};

export default BulkImport;
